/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-negated-condition */

import React, { useEffect, useState } from 'react';
import { useQuery } from '@apollo/client';
import { FSBTable } from 'components/common/FSBTable';
import {
  defaultDeleteFilter,
  showSequenceNumber,
  vendorColumns,
} from 'utils/constants';
import { VENDORS_QUERY, VENDORS_QUERY_SUBSCRIPTION } from 'utils/CommonGraphQL';
import { cloneDeep, isEmpty } from 'lodash';
import Loader from 'components/common/Loader';
import { FaChevronDown } from 'react-icons/fa6';
import '../../../../dashboard-mobile.css';

/* ─── helpers ─── */
const getVendorName = (trader) =>
  trader?.entityName ||
  trader?.tradeName ||
  [trader?.firstName, trader?.lastName].filter(Boolean).join(' ') ||
  'Not Available';

const getVendorPhone = (trader) => {
  if (!trader?.phone) return 'No phone';
  const cc = trader?.callingCode?.callingCode || '';
  return `${cc}${cc ? '-' : ''}${trader.phone}`;
};

const getKycTone = (status) => {
  const s = `${status || ''}`.toLowerCase();
  if (s.includes('accepted') || s.includes('approved')) return 'success';
  if (s.includes('pending')) return 'warning';
  if (s.includes('rejected')) return 'danger';
  return 'muted';
};

const getStatusTone = (status) => {
  const s = `${status || ''}`.toLowerCase();
  if (s.includes('active')) return 'success';
  if (s.includes('inactive') || s.includes('deactivated')) return 'danger';
  return 'muted';
};

const formatDate = (val) => {
  if (!val) return 'N/A';
  return new Date(val).toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

const VENDOR_TABS = ['Overview', 'Details'];

/* ─── component ─── */
const ALL = () => {
  const [filters, setFilters] = useState({
    filter: [
      { ...defaultDeleteFilter },
      { key: 'status', value: 'ACTIVE', type: 'STRING', op: 'EQ' },
    ],
    pageNo: 1,
    pageSize: 10,
    sortCol: 'createdAt',
    sortDirection: 'DESC',
    pagination: true,
  });

  const [expandedCardId, setExpandedCardId] = useState(null);
  const [activeTabs, setActiveTabs] = useState({});

  const { data, loading, error, subscribeToMore } = useQuery(VENDORS_QUERY, {
    variables: { ...filters },
  });

  const findIndexOfItem = (item) =>
    vendorColumns.findIndex((itm) => itm.accessorKey === item);

  vendorColumns[findIndexOfItem('SNo')].cell = (cell) =>
    showSequenceNumber(cell, filters);

  useEffect(() => {
    const deletedObj = filters.filter.filter((val) => val.key !== 'isDeleted');
    const unsubscribe = subscribeToMore({
      document: VENDORS_QUERY_SUBSCRIPTION,
      variables: { ...filters, filter: deletedObj },
      updateQuery: (prev, { subscriptionData }) => {
        const newData = cloneDeep(prev);
        const indexFound = newData?.traders?.traders.findIndex(
          (val) => val.id === subscriptionData.data.traders.trader.id,
        );
        if (!prev || isEmpty(prev)) return '';
        if (!subscriptionData.data) return prev;
        if (subscriptionData.data.traders.action === 'CREATED') {
          const newTrader = subscriptionData.data.traders.trader;
          const updatedTraders = [newTrader, ...newData.traders.traders];
          const newTotalCount = prev.traders.totalCount + 1;
          const newTotalPages = Math.ceil(newTotalCount / filters.pageSize);
          newData.traders = {
            ...newData.traders,
            totalCount: newTotalCount,
            totalPages: newTotalPages,
            traders: updatedTraders,
          };
        }
        if (subscriptionData.data.traders.action === 'UPDATED') {
          newData.traders.traders[indexFound] =
            subscriptionData.data.traders.trader;
        }
        if (subscriptionData.data.traders.action === 'DELETED') {
          const { id } = subscriptionData.data.traders.trader;
          const updatedTraders = prev.traders.traders.filter(
            (val) => val.id !== id,
          );
          const newTotalCount = prev.traders.totalCount - 1;
          const newTotalPages = Math.ceil(newTotalCount / filters.pageSize);
          newData.traders = {
            ...newData.traders,
            totalCount: newTotalCount,
            totalPages: newTotalPages,
            traders: updatedTraders,
          };
        }
        return newData;
      },
    });
    return () => unsubscribe();
  }, []);

  if (error) return `There is an error on this page : ${error.message} `;
  if (loading) return <Loader />;
  if (!data?.traders?.traders) return 'Traders not available';

  const { totalCount, totalPages } = data.traders;

  const handlePageChange = ({ pageSize, pageNo }) => {
    setFilters({
      ...filters,
      pageNo: totalCount < pageSize ? 1 : pageNo,
      pageSize,
    });
  };

  const onSortChange = (sort) => {
    setFilters({
      ...filters,
      sortCol: sort()[0].id,
      sortDirection: sort()[0].desc ? 'DESC' : 'ASC',
    });
  };

  const sortBy = [
    filters.sortCol === null
      ? { id: null, desc: null }
      : { id: filters.sortCol, desc: filters.sortDirection === 'DESC' },
  ];

  const traders = data?.traders?.traders || [];

  return (
    <>
      {/* ── Mobile Card List ── */}
      <div className="dashboard-mobile-list">
        {traders.map((trader, index) => {
          const cardId = trader?.id || `vendor-${index}`;
          const isExpanded = expandedCardId === cardId;
          const activeTab = activeTabs[cardId] || VENDOR_TABS[0];
          const seqNo =
            ((filters.pageNo || 1) - 1) * (filters.pageSize || 10) + index + 1;

          const overviewRows = [
            { label: 'S.No', value: seqNo },
            { label: 'Business', value: getVendorName(trader) },
            { label: 'Phone', value: getVendorPhone(trader) },
            { label: 'Status', value: trader?.status || 'N/A', tone: 'status' },
            { label: 'KYC', value: trader?.kycStatus || 'N/A', tone: 'kyc' },
          ];

          const detailRows = [
            { label: 'Email', value: trader?.email || 'N/A' },
            { label: 'Trade Name', value: trader?.tradeName || 'N/A' },
            { label: 'City', value: trader?.city?.cityName || 'N/A' },
            { label: 'State', value: trader?.state?.stateName || 'N/A' },
            { label: 'Joined', value: formatDate(trader?.createdAt) },
          ];

          const activeSection =
            activeTab === 'Overview' ? overviewRows : detailRows;

          return (
            <div className="dashboard-mobile-card" key={cardId}>
              <div className="dashboard-mobile-card__summary">
                <div className="dashboard-mobile-card__identity">
                  <span className="dashboard-mobile-card__name">
                    {getVendorName(trader)}
                  </span>
                  <div className="dashboard-mobile-card__meta">
                    {getVendorPhone(trader)}
                    {trader?.email ? ` | ${trader.email}` : ''}
                  </div>
                </div>
                <div className="dashboard-mobile-card__aside">
                  <span
                    className={`dashboard-mobile-card__pill ${getKycTone(trader?.kycStatus)}`}
                  >
                    {trader?.kycStatus || 'N/A'}
                  </span>
                  <div className="dashboard-mobile-card__tools">
                    <button
                      type="button"
                      className={`dashboard-mobile-card__expand-toggle ${isExpanded ? 'is-open' : ''}`}
                      onClick={() =>
                        setExpandedCardId((prev) =>
                          prev === cardId ? null : cardId,
                        )
                      }
                      aria-expanded={isExpanded}
                      aria-label={isExpanded ? 'Collapse' : 'Expand'}
                    >
                      <FaChevronDown />
                    </button>
                  </div>
                </div>
              </div>

              {isExpanded && (
                <div className="dashboard-mobile-card__body">
                  <div className="dashboard-mobile-card__tabs">
                    {VENDOR_TABS.map((tab) => (
                      <button
                        key={tab}
                        type="button"
                        className={`dashboard-mobile-card__tab ${activeTab === tab ? 'active' : ''}`}
                        onClick={() =>
                          setActiveTabs((prev) => ({ ...prev, [cardId]: tab }))
                        }
                      >
                        {tab}
                      </button>
                    ))}
                  </div>
                  <div className="dashboard-mobile-card__detail-list">
                    {activeSection.map((item) => (
                      <div
                        className="dashboard-mobile-card__detail-row"
                        key={item.label}
                      >
                        <div className="dashboard-mobile-card__detail-label">
                          {item.label}
                        </div>
                        <div className="dashboard-mobile-card__detail-value">
                          {item.tone === 'status' ? (
                            <span
                              className={`dashboard-mobile-card__pill ${getStatusTone(item.value)}`}
                            >
                              {item.value}
                            </span>
                          ) : item.tone === 'kyc' ? (
                            <span
                              className={`dashboard-mobile-card__pill ${getKycTone(item.value)}`}
                            >
                              {item.value}
                            </span>
                          ) : (
                            item.value
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* ── Mobile Pagination ── */}
      {totalPages ? (
        <div className="dashboard-mobile-pagination">
          <span className="dashboard-mobile-pagination__count">
            {filters.pageNo} of {totalPages}
          </span>
          <select
            className="page-select form-select table-select f-12 fw-normal dashboard-mobile-pagination__select"
            value={filters.pageSize}
            onChange={(e) =>
              handlePageChange({ pageNo: 1, pageSize: Number(e.target.value) })
            }
          >
            {[10, 20, 30, 40, 50].map((size) => (
              <option key={size} value={size}>
                {size} per page
              </option>
            ))}
          </select>
          <div className="dashboard-mobile-pagination__actions">
            <button
              type="button"
              onClick={() =>
                handlePageChange({ pageNo: 1, pageSize: filters.pageSize })
              }
              disabled={filters.pageNo <= 1}
            >
              {'<<'}
            </button>
            <button
              type="button"
              onClick={() =>
                handlePageChange({
                  pageNo: filters.pageNo - 1,
                  pageSize: filters.pageSize,
                })
              }
              disabled={filters.pageNo <= 1}
            >
              {'<'}
            </button>
            <button
              type="button"
              onClick={() =>
                handlePageChange({
                  pageNo: filters.pageNo + 1,
                  pageSize: filters.pageSize,
                })
              }
              disabled={filters.pageNo >= totalPages}
            >
              {'>'}
            </button>
            <button
              type="button"
              onClick={() =>
                handlePageChange({
                  pageNo: totalPages,
                  pageSize: filters.pageSize,
                })
              }
              disabled={filters.pageNo >= totalPages}
            >
              {'>>'}
            </button>
          </div>
        </div>
      ) : null}

      {/* ── Desktop Table (hidden on mobile) ── */}
      <div className="dashboard-mobile-hide-table">
        <FSBTable
          data={data?.traders?.traders}
          columns={vendorColumns}
          totalCount={totalCount || 0}
          totalPages={totalPages || 0}
          currentPageNo={filters.pageNo}
          pageSize={filters.pageSize}
          keyField="id"
          showSearch={false}
          showFilters={false}
          showDelete={false}
          showPagination={false}
          remote
          striped={false}
          hover={false}
          bordered={false}
          wrapperClass="table-responsive word-break"
          handlePageChange={handlePageChange}
          onSortChange={onSortChange}
          sorting={sortBy}
          searchPlaceholder="Search Vendors"
        />
      </div>
    </>
  );
};

export default ALL;
